import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    userStatusResponseSchema,
    userSchema,
    userMetaSchema,
    createAvatarUploadUrlBodySchema,
    createAvatarUploadUrlResponseSchema,
    updateAvatarBodySchema,
    updateUserBodySchema,
    updateUserMetaBodySchema,
    updateProfileBodySchema,
    addPhoneNumberBodySchema,
    setUserProductTipBodySchema,
    userProductTipsResponseSchema,
} from "../schemas/user.schema";
import { UserEntitiesResponseDto } from "../dto/onboarding/user-entities-response.dto";
import {
    FinancialAccountSourceType,
    FinancialAccountType,
} from "../types/financialAccount";
import { ConnectionProvider } from "../types/integrationConnection";

const c = initContract();

export interface UserFinancialAccountQueryItem {
    id: number;
    entityId: number | null;
    entityName: string;
    name: string | null;
    institutionName: string | null;
    accountNumberMask: string | null;
    financialType: FinancialAccountType;
    sourceType: FinancialAccountSourceType;
    disabled: boolean;
    integrationAccounts: Array<{
        id: string;
        sourceType: FinancialAccountSourceType;
        connectionId: number | null;
        integrationConnectionId: string | null;
        balanceSyncedAt: string | null;
        transactionsSyncedAt: string | null;
        invoicesSyncedAt: string | null;
        statementsSyncedAt: string | null;
        lastStatementDate: string | null;
        initialSyncStatus: string | null;
        integrationConnection: {
            provider: ConnectionProvider;
            label: string | null;
            institutionName: string | null;
            error: string | null;
        } | null;
    }>;
}

export interface UserFinancialAccountQueryResponse {
    accounts: UserFinancialAccountQueryItem[];
    total: number;
}

export const userContract = c.router(
    {
        getStatus: {
            method: "GET",
            path: "/status",
            responses: {
                200: userStatusResponseSchema,
            },
        },
        getEntities: {
            method: "GET",
            path: "/workspaces/:workspaceId/entities",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<UserEntitiesResponseDto>(),
                404: ErrorMessageSchema,
            },
        },
        getFinancialAccounts: {
            method: "GET",
            path: "/workspaces/:workspaceId/financial-accounts",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                search: z.string().trim().min(1).max(200).optional(),
                limit: z.coerce.number().int().min(1).max(500).default(100),
                offset: z.coerce.number().int().min(0).default(0),
            }),
            responses: {
                200: c.type<UserFinancialAccountQueryResponse>(),
                404: ErrorMessageSchema,
            },
        },
        createAvatarUploadUrl: {
            method: "POST",
            path: "/avatar/upload-url",
            body: createAvatarUploadUrlBodySchema,
            responses: {
                201: createAvatarUploadUrlResponseSchema,
            },
        },
        updateAvatar: {
            method: "PUT",
            path: "/avatar",
            body: updateAvatarBodySchema,
            responses: {
                200: userSchema,
            },
        },
        update: {
            method: "PATCH",
            path: "/",
            body: updateUserBodySchema,
            responses: {
                200: userSchema,
                400: ErrorMessageSchema,
            },
        },
        getMeta: {
            method: "GET",
            path: "/meta",
            responses: {
                200: userMetaSchema,
            },
        },
        updateMeta: {
            method: "PATCH",
            path: "/meta",
            body: updateUserMetaBodySchema,
            responses: {
                200: userMetaSchema,
                400: ErrorMessageSchema,
            },
        },
        updateProfile: {
            method: "PATCH",
            path: "/profile",
            body: updateProfileBodySchema,
            responses: {
                200: userSchema,
                400: ErrorMessageSchema,
            },
        },
        addPhoneNumber: {
            method: "POST",
            path: "/profile/phone-number",
            body: addPhoneNumberBodySchema,
            responses: {
                200: userSchema,
                400: ErrorMessageSchema,
            },
        },
        setProductTip: {
            method: "PATCH",
            path: "/product-tips",
            body: setUserProductTipBodySchema,
            responses: {
                200: userProductTipsResponseSchema,
                400: ErrorMessageSchema,
            },
        },
        clearInitialProductTips: {
            method: "DELETE",
            path: "/product-tips/initial",
            body: z.object({}),
            responses: {
                200: userProductTipsResponseSchema,
            },
        },
    },
    {
        pathPrefix: "/user",
    },
);
